import { Component, Input, OnDestroy, OnInit, Output, EventEmitter, ViewChild, ChangeDetectorRef } from '@angular/core';
import { ControlValueAccessor, NG_VALUE_ACCESSOR } from '@angular/forms';
import { BehaviorSubject, Observable, of, Subject, zip } from 'rxjs';
import {
  DatasetSelectedRecipientFilter,
  DistributionListCount,
  MassNotificationSelectedRecipient,
  MassNotificationTemplate,
  MassRecipientType
} from 'src/app/modules/communications/store/communications/communications.model';
import { FieldDataType, TableFilter } from 'src/app/modules/core/store/models';
import { RecipientSelectionService } from 'src/app/modules/communications/services/recipient-selection-service';
import { TemplateDesignerService } from '@app/modules/communications/services/template-designer.service';
import { CommunicationsApiService } from '@app/modules/communications/store/communications/communications-api.service';
import { RecipientListSelectComponent } from '../recipient-list-select/recipient-list-select.component';
import { GUID_EMPTY } from '@app/core/constants';

@Component({
    selector: 'topo-comms-recipients-from-template',
    templateUrl: './recipients-from-template.component.html',
    styleUrls: ['./recipients-from-template.component.scss'],
    providers: [
        {
            provide: NG_VALUE_ACCESSOR,
            multi: true,
            useExisting: RecipientsFromTemplateComponent
        }
    ],
    standalone: false
})
export class RecipientsFromTemplateComponent implements OnInit, OnDestroy {
  @Input() set template(value: MassNotificationTemplate) {
    this.selectedTemplateRecipients = value?.recipientSettings?.recipients;
    this.templateDatasetsSelected = value?.recipientSettings?.datasetRecipients?.filter(d => d.documentCount > 0);

    this.templateDatasetsSelected?.forEach(d => {
      this.recipientSelectionService.getTableFilterDisplay(d, null).subscribe(value => {
        d.filterDisplay = value;
      });
      d.relatedAssetName = this.getRelatedAssetsName(d);
    });
    this.getTemplateDistListMembersCount();
    this.calculateTotalRecipients();
  }

  @Input() showSelectedDetails: boolean;

  @ViewChild('listSelect') listSelection: RecipientListSelectComponent;

  destroy$: Subject<void> = new Subject<void>();
  templateDatasetsSelected: DatasetSelectedRecipientFilterEx[];
  selectedTemplateRecipients?: MassNotificationSelectedRecipient[];
  templateDistRecipients: string[];
  templateUserRecipients: string[];
  templateGroupRecipients: string[];
  addedTemplateRecipients: number;

  recipientsSelectionActive: boolean;
  selectedRecipientTypeUId: string;
  recipientFilter: DatasetSelectedRecipientFilter;
  distMemberContacts: { [uid: string]: number };

  constructor(
    private recipientSelectionService: RecipientSelectionService,
    private templateDesignerService: TemplateDesignerService,
    private changeDetector: ChangeDetectorRef
  ) {}

  ngOnInit(): void {
    this.recipientSelectionService.init();
    this.templateDesignerService.initDatasets();
  }

  ngOnDestroy(): void {
    this.destroy$.next();
    this.destroy$.complete();
  }

  private getRelatedAssetsName(datasetInfo: DatasetSelectedRecipientFilterEx): string {
    const dataset = this.templateDesignerService.datasets?.find(d => d.uId === datasetInfo?.datasetUId);
    const field = dataset?.schema?.fields.find(f => f.dataType === FieldDataType.ReferenceCriteria);
    if (!!field?.settings?.targetDatasetUId) {
      const targetDataset = this.templateDesignerService.datasets?.find(d => d.uId === field?.settings?.targetDatasetUId);
      return targetDataset?.name;
    } else {
      return null;
    }
  }

  private calculateTotalRecipients() {
    this.templateDistRecipients = [];
    this.selectedTemplateRecipients
      ?.filter(r => r.type === MassRecipientType.DistributionList)
      ?.forEach(r => {
        this.templateDistRecipients.push(r.uId);
      });
    this.templateUserRecipients = [];
    this.selectedTemplateRecipients
      ?.filter(r => r.type === MassRecipientType.User)
      ?.forEach(r => {
        this.templateUserRecipients.push(r.uId);
      });

    this.templateGroupRecipients = [];
    this.selectedTemplateRecipients
      ?.filter(r => r.type === MassRecipientType.Group)
      ?.forEach(r => {
        this.templateGroupRecipients.push(r.uId);
      });

    this.addedTemplateRecipients = this.selectedTemplateRecipients?.filter(r => r.type !== MassRecipientType.DistributionList).length ?? 0;
    this.templateDatasetsSelected?.forEach(r => {
      r.contactCount = r?.documentCount * this.templateDesignerService.getNumberOfContactFields(r.datasetUId);
      this.addedTemplateRecipients += r.contactCount;
    });

    this.addedTemplateRecipients += this.totalDistListContacts;

    this.changeDetector.detectChanges();
  }

  private getTemplateDistListMembersCount(): void {
    this.distMemberContacts = {};
    this.recipientSelectionService.getDistListMembersCount(this.selectedTemplateRecipients).subscribe(resp => {
      // * NOTE * bandaid solution to handling stringified json references that aren't deleted
      // API returns empty guid for the datasetUId when the dist list is not found
      const notFoundUIds = new Set<string>();

      resp.forEach(r => {
        if (r.datasetUId === GUID_EMPTY) {
          notFoundUIds.add(r.distributionListUId);
        } else {
          this.distMemberContacts[r.distributionListUId] = r.count * this.templateDesignerService.getNumberOfContactFields(r.datasetUId);
        }
      });

      this.templateDistRecipients = this.templateDistRecipients?.filter(uId => !notFoundUIds.has(uId)) ?? [];
      this.selectedTemplateRecipients = this.selectedTemplateRecipients?.filter(r => !notFoundUIds.has(r.uId)) ?? [];

      this.calculateTotalRecipients();
    });
  }

  get totalDistListContacts(): number {
    let total = 0;
    if (!!this.distMemberContacts) {
      Object.values(this.distMemberContacts).forEach(v => { total += v });
    }
    return total;
  }

  onUserDetails(): void {
    this.selectedRecipientTypeUId = this.listSelection?.userRecipientTypeUId;
    this.recipientsSelectionActive = true;
  }

  onDistDetails(): void {
    this.selectedRecipientTypeUId = this.listSelection?.distRecipientTypeUId;
    this.recipientsSelectionActive = true;
  }

  onDatasetDetails(uId: string): void {
    this.recipientFilter = this.templateDatasetsSelected?.find(d => d.uId == uId);
    this.selectedRecipientTypeUId = this.recipientFilter?.datasetUId;
    this.recipientsSelectionActive = true;
  }

  recipientsClosed(): void {
    this.recipientsSelectionActive = false;
  }

  getSelectedUIds = (type: MassRecipientType, parentUId: string): string[] => {
    if (type === MassRecipientType.Document) {
      const selectedDataset = this.templateDatasetsSelected?.find(d => d.datasetUId === parentUId);
      return selectedDataset?.selectedUIds;
    } else {
      return this.selectedTemplateRecipients?.filter(d => d.type === type)?.map(d => d.uId);
    }
  };
}

export interface DatasetSelectedRecipientFilterEx extends DatasetSelectedRecipientFilter {
  filterDisplay?: string;
  relatedAssetName?: string;
  contactCount?: number;
}
